import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Hash,
  Loader2,
  Send,
  FileText,
  Download,
  Wallet,
  Mail,
  Phone,
  User as UserIcon,
  Users as TeamIcon,
  Pencil,
  Plus,
  Trash2,
  Check,
  X as XIcon,
  Printer,
} from "lucide-react";
import Pill from "@/components/Pill";
import FileUpload from "@/components/FileUpload";
import { useAuth } from "@/context/AuthContext";
import { useRefData } from "@/context/RefDataContext";
import { can, isResourceReadOnly, useEnabledModules } from "@/lib/permissions";
import { toast } from "sonner";
import RaiseOPADialog from "@/components/RaiseOPADialog";
import OPAStatusTiles from "@/components/OPAStatusTiles";
import useMyWallet from "@/hooks/useMyWallet";
import ReplyAssignControls from "@/components/ReplyAssignControls";
import ReplyAssignTimelineBadges from "@/components/ReplyAssignTimelineBadges";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleString();
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function initials(name = "") {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?"
  );
}

export default function IssueDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const ref = useRefData();

  const [issue, setIssue] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opaTarget, setOpaTarget] = useState(null);
  const [opaRefreshKey, setOpaRefreshKey] = useState(0);
  const { wallet: myWallet } = useMyWallet();

  const [message, setMessage] = useState("");
  const [statusChange, setStatusChange] = useState("");
  const [assignTo, setAssignTo] = useState("");
  const [forwardTo, setForwardTo] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  // Inline contacts editor state — flips popover between view (list of
  // contact cards) and edit (per-row Input fields with add/remove).
  // `draftContacts` is a working copy so the user can cancel without
  // mutating the persisted list.
  const [contactsEditing, setContactsEditing] = useState(false);
  const [contactsPopoverOpen, setContactsPopoverOpen] = useState(false);
  const [draftContacts, setDraftContacts] = useState([]);
  const [contactsSaving, setContactsSaving] = useState(false);
  // OPA lightbox — collapses the 3 tiles + per-user breakdown behind a
  // single "OPA Summary" trigger. Kept mounted while open so refreshes on
  // reply-driven re-render behave the same as when it lived inline.
  const [opaSummaryOpen, setOpaSummaryOpen] = useState(false);

  const moduleMeta = useEnabledModules();
  const canReply = !isResourceReadOnly(moduleMeta, "issues") && can(user, "issues", "update");

  const load = useCallback(async () => {
    try {
      const [i, r] = await Promise.all([
        api.get(`/issues/${id}`),
        api.get(`/issues/${id}/replies`),
      ]);
      setIssue(i.data);
      setReplies(r.data);
    } catch (e) {
      toast.error("Ticket not found");
      navigate("/assignments");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    if (!message.trim()) {
      toast.error("Message is required");
      return;
    }
    setSending(true);
    try {
      await api.post(`/issues/${id}/replies`, {
        message: message.trim(),
        status_change: statusChange || null,
        assigned_user_id: assignTo || null,
        team_id: forwardTo || null,
        files,
      });
      setMessage("");
      setStatusChange("");
      setAssignTo("");
      setForwardTo("");
      setFiles([]);
      toast.success("Reply added");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Reply failed");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 text-sm" data-testid="issue-detail-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading ticket…
      </div>
    );
  }
  if (!issue) return null;

  const projectLabel = ref.projects?.find((p) => p.value === issue.project_id)?.label;
  const teamLabel = ref.teams?.find((t) => t.value === issue.team_id)?.label;
  const assetLabel = ref.assets?.find((a) => a.value === issue.asset_id)?.label;
  const serviceLabel = ref.services?.find((s) => s.value === issue.service_id)?.label;
  const contacts = Array.isArray(issue.contact_persons) ? issue.contact_persons : [];
  const attachments = Array.isArray(issue.files) ? issue.files : [];

  // Users who can update the ticket (respecting read-only mode) may edit
  // contacts. Same gate that guards Reply.
  const canEditContacts = canReply;

  const openContactsEditor = () => {
    // Deep-copy to avoid mutating the persisted array; also seed one blank
    // row when there are no contacts yet so the user has something to type
    // into immediately.
    setDraftContacts(
      contacts.length > 0
        ? contacts.map((c) => ({ name: c.name || "", email: c.email || "", phone: c.phone || "" }))
        : [{ name: "", email: "", phone: "" }]
    );
    setContactsEditing(true);
  };
  const updateDraft = (idx, patch) =>
    setDraftContacts((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeDraft = (idx) => setDraftContacts((rows) => rows.filter((_, i) => i !== idx));
  const addDraft = () =>
    setDraftContacts((rows) => [...rows, { name: "", email: "", phone: "" }]);
  const saveContacts = async () => {
    // Drop entirely-empty rows before persisting so we don't store `{}`.
    const cleaned = draftContacts
      .map((c) => ({
        name: (c.name || "").trim(),
        email: (c.email || "").trim(),
        phone: (c.phone || "").trim(),
      }))
      .filter((c) => c.name || c.email || c.phone);
    setContactsSaving(true);
    try {
      const res = await api.patch(`/issues/${id}`, { contact_persons: cleaned });
      setIssue(res.data);
      setContactsEditing(false);
      setContactsPopoverOpen(false);
      toast.success("Contacts updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not save contacts");
    } finally {
      setContactsSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="issue-detail-page">
      {/* Back link + header sit in a tight cluster (small gap-2 instead of
          the page's default 2rem space-y-8) so the ticket code doesn't feel
          orphaned from its breadcrumb. */}
      <div className="space-y-2">
        <Link
          to="/assignments"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
          data-testid="issue-detail-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Assignments
        </Link>

        <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-overline mb-2 flex items-center gap-2">
            <Hash className="w-3 h-3" />
            <span className="font-mono normal-case tracking-normal" data-testid="issue-detail-code">
              {issue.code}
            </span>
          </div>
          <h1
            className="text-3xl font-semibold tracking-tight text-white"
            style={{ fontFamily: "Outfit" }}
            data-testid="issue-detail-subject"
          >
            {issue.subject}
          </h1>
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <Popover
              open={contactsPopoverOpen}
              onOpenChange={(open) => {
                setContactsPopoverOpen(open);
                if (!open) setContactsEditing(false);
              }}
            >
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300 hover:underline"
                  data-testid="issue-detail-contacts-link"
                >
                  <UserIcon className="w-3 h-3" />
                  Contacts ({contacts.length})
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-96 bg-zinc-900 border-white/10 text-zinc-100 p-3"
                data-testid="issue-detail-contacts-popover"
              >
                {!contactsEditing ? (
                  // ------ VIEW MODE ------
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <div className="label-overline">Contact Persons</div>
                      {canEditContacts && (
                        <button
                          type="button"
                          onClick={openContactsEditor}
                          className="inline-flex items-center gap-1 text-[11px] text-sky-400 hover:text-sky-300"
                          data-testid="issue-detail-contacts-edit"
                        >
                          <Pencil className="w-3 h-3" />
                          {contacts.length === 0 ? "Add contacts" : "Edit"}
                        </button>
                      )}
                    </div>
                    {contacts.length === 0 ? (
                      <p className="text-xs text-zinc-500 italic px-1 py-3">
                        No contacts yet.{canEditContacts ? " Click Add contacts to create one." : ""}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {contacts.map((c, i) => (
                          <div
                            key={i}
                            className="rounded-md border border-white/5 bg-zinc-950 px-2.5 py-2 text-xs space-y-1"
                            data-testid={`issue-contact-${i}`}
                          >
                            <div className="flex items-center gap-1.5 text-zinc-100">
                              <UserIcon className="w-3 h-3 text-zinc-500" />
                              <span className="truncate">{c.name || "—"}</span>
                            </div>
                            {c.email && (
                              <a
                                href={`mailto:${c.email}`}
                                className="flex items-center gap-1.5 text-sky-400 hover:underline break-all"
                              >
                                <Mail className="w-3 h-3" /> {c.email}
                              </a>
                            )}
                            {c.phone && (
                              <div className="flex items-center gap-1.5 text-zinc-300 font-mono">
                                <Phone className="w-3 h-3 text-zinc-500" /> {c.phone}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  // ------ EDIT MODE ------
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <div className="label-overline">Edit Contacts</div>
                      <button
                        type="button"
                        onClick={addDraft}
                        className="inline-flex items-center gap-1 text-[11px] text-sky-400 hover:text-sky-300"
                        data-testid="issue-detail-contacts-add"
                      >
                        <Plus className="w-3 h-3" /> Add
                      </button>
                    </div>
                    <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                      {draftContacts.length === 0 && (
                        <p className="text-xs text-zinc-500 italic px-1 py-3">
                          No rows. Click Add to create one.
                        </p>
                      )}
                      {draftContacts.map((c, i) => (
                        <div
                          key={i}
                          className="rounded-md border border-white/5 bg-zinc-950 p-2 space-y-1.5"
                          data-testid={`issue-detail-contact-row-${i}`}
                        >
                          <div className="flex items-center gap-1.5">
                            <Input
                              value={c.name}
                              onChange={(e) => updateDraft(i, { name: e.target.value })}
                              placeholder="Name"
                              className="bg-zinc-950 border-white/10 text-white text-xs h-8"
                              data-testid={`issue-detail-contact-name-${i}`}
                            />
                            <button
                              type="button"
                              onClick={() => removeDraft(i)}
                              className="h-8 px-1.5 text-zinc-500 hover:text-red-400"
                              title="Remove contact"
                              data-testid={`issue-detail-contact-remove-${i}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <Input
                            type="email"
                            value={c.email}
                            onChange={(e) => updateDraft(i, { email: e.target.value })}
                            placeholder="Email"
                            className="bg-zinc-950 border-white/10 text-white text-xs h-8"
                            data-testid={`issue-detail-contact-email-${i}`}
                          />
                          <Input
                            value={c.phone}
                            onChange={(e) => updateDraft(i, { phone: e.target.value })}
                            placeholder="Phone"
                            className="bg-zinc-950 border-white/10 text-white text-xs h-8"
                            data-testid={`issue-detail-contact-phone-${i}`}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-end gap-2 mt-3 pt-2 border-t border-white/5">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setContactsEditing(false)}
                        disabled={contactsSaving}
                        className="h-7 text-xs text-zinc-400 hover:text-white"
                        data-testid="issue-detail-contacts-cancel"
                      >
                        <XIcon className="w-3.5 h-3.5 mr-1" /> Cancel
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={saveContacts}
                        disabled={contactsSaving}
                        className="h-7 text-xs bg-sky-500 hover:bg-sky-400 text-white"
                        data-testid="issue-detail-contacts-save"
                      >
                        {contactsSaving ? (
                          <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Saving…</>
                        ) : (
                          <><Check className="w-3.5 h-3.5 mr-1" /> Save</>
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </PopoverContent>
            </Popover>
            <Pill kind="priority" value={issue.priority} testid="issue-detail-priority" />
            <Pill kind="status" value={issue.status} testid="issue-detail-status" />
            {projectLabel && (
              <Link
                to={`/projects/${issue.project_id}`}
                className="text-xs text-sky-400 hover:underline"
              >
                CDARK: {projectLabel}
              </Link>
            )}
            {teamLabel && (
              <span className="text-xs text-zinc-500 inline-flex items-center gap-1">
                <TeamIcon className="w-3 h-3" /> Team: {teamLabel}
              </span>
            )}
            <span className="text-xs text-zinc-500">Created: {fmtDate(issue.created_at)}</span>
            {/* OPA Summary trigger sits at the end of the metadata row per
                UX spec — pushed to the right with ml-auto so it hugs the
                far edge on wider screens and wraps cleanly on narrow ones. */}
            <button
              type="button"
              onClick={() => setOpaSummaryOpen(true)}
              className="ml-auto inline-flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300 hover:underline"
              data-testid="issue-detail-opa-summary-link"
            >
              <Wallet className="w-3 h-3" />
              OPA Summary
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => {
              // Open the PDF in a new tab. We pass the JWT as a query param
              // because a plain `<a target="_blank">` navigation can't attach
              // the `Authorization` header — the backend accepts both.
              const token = encodeURIComponent(localStorage.getItem("ops_token") || "");
              const url = `${api.defaults.baseURL}/issues/${issue.id}/pdf?auth=${token}`;
              window.open(url, "_blank", "noopener");
            }}
            className="border-white/15 bg-zinc-900 text-zinc-200 hover:bg-zinc-800"
            data-testid="issue-detail-print"
            title="Download / print a PDF copy of this ticket"
            aria-label="Print ticket"
          >
            <Printer className="w-4 h-4" />
          </Button>
          <Button
            onClick={() => {
              if (!myWallet) {
                toast.error("No wallet tagged to your account. Contact your manager to enable Raise OPA.");
                return;
              }
              setOpaTarget({ type: "ticket", id: issue.id, code: issue.code, subject: issue.subject });
            }}
            disabled={!myWallet}
            className="bg-emerald-500 text-black hover:bg-emerald-400 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="issue-detail-raise-opa"
            title={myWallet ? "Raise an OPA against this ticket" : "No wallet tagged — contact your manager"}
          >
            <Wallet className="w-4 h-4 mr-1.5" /> Raise OPA
          </Button>
        </div>
      </header>
      </div>

      <RaiseOPADialog target={opaTarget} onClose={() => setOpaTarget(null)} onCreated={() => setOpaRefreshKey((k) => k + 1)} />

      {/* OPA Summary lightbox — trigger lives in the header metadata row.
          Renders the same `OPAStatusTiles` component so role/scoping logic
          stays intact; `refreshKey` still propagates so newly-raised OPAs
          reflect the next time the dialog opens. */}
      <Dialog open={opaSummaryOpen} onOpenChange={setOpaSummaryOpen}>
        <DialogContent
          className="max-w-3xl bg-zinc-950 border-white/10 text-zinc-100"
          data-testid="issue-detail-opa-summary-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white">OPA Summary</DialogTitle>
          </DialogHeader>
          <OPAStatusTiles issueId={issue.id} refreshKey={opaRefreshKey} />
        </DialogContent>
      </Dialog>

      {(issue.description || assetLabel || serviceLabel || attachments.length > 0) && (
        <section
          className="bg-zinc-900 border border-white/10 rounded-lg p-5"
          data-testid="issue-detail-description"
        >
          {/* Header row: overline + inline Related Asset / Service / Attachments.
              All three sit on the same line as the "Ticket Summary" label per
              layout spec; the attachments trigger opens a popover with the
              downloadable file list. */}
          <div className="flex items-center gap-4 flex-wrap mb-3">
            <div className="label-overline">Ticket Summary</div>
            {assetLabel && (
              <Link
                to={`/assets/${issue.asset_id}`}
                className="text-xs text-sky-400 hover:underline inline-flex items-center gap-1"
                data-testid="issue-detail-related-asset"
              >
                <span className="text-zinc-500">Related Asset:</span>
                <span className="truncate max-w-[220px]">{assetLabel}</span>
              </Link>
            )}
            {serviceLabel && (
              <Link
                to={`/services/${issue.service_id}`}
                className="text-xs text-sky-400 hover:underline inline-flex items-center gap-1"
                data-testid="issue-detail-related-service"
              >
                <span className="text-zinc-500">Related Service:</span>
                <span className="truncate max-w-[220px]">{serviceLabel}</span>
              </Link>
            )}
            {attachments.length > 0 && (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="text-xs text-sky-400 hover:underline inline-flex items-center gap-1"
                    data-testid="issue-detail-attachments-link"
                  >
                    <FileText className="w-3 h-3" />
                    Attachments ({attachments.length})
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-80 bg-zinc-900 border-white/10 text-zinc-100 p-2"
                  data-testid="issue-detail-attachments-popover"
                >
                  <div className="label-overline px-1 mb-2">Attachments</div>
                  <ul className="space-y-1">
                    {attachments.map((f) => (
                      <li
                        key={f.id}
                        className="flex items-center justify-between rounded-md bg-zinc-950 border border-white/5 px-2 py-1.5 text-xs"
                      >
                        <span className="flex items-center gap-1.5 text-zinc-200 truncate">
                          <FileText className="w-3.5 h-3.5 text-zinc-500" />
                          <span className="truncate">{f.name || f.filename}</span>
                        </span>
                        <a
                          href={`${api.defaults.baseURL}/files/${f.id}/download?auth=${encodeURIComponent(localStorage.getItem("ops_token") || "")}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sky-400 hover:underline inline-flex items-center gap-1 ml-2 shrink-0"
                          data-testid={`issue-detail-attachment-download-${f.id}`}
                        >
                          <Download className="w-3 h-3" /> Get
                        </a>
                      </li>
                    ))}
                  </ul>
                </PopoverContent>
              </Popover>
            )}
          </div>
          {issue.description && (
            <p className="text-sm text-zinc-200 whitespace-pre-wrap">{issue.description}</p>
          )}
        </section>
      )}

      {/* Timeline — placed above the reply form so users see prior context
          before composing. */}
      <section data-testid="issue-timeline">
        <div className="label-overline mb-4">Reply timeline ({replies.length})</div>
        {replies.length === 0 ? (
          <div className="bg-zinc-900 border border-white/10 rounded-lg p-8 text-center text-sm text-zinc-500">
            No replies yet.
          </div>
        ) : (
          <ol className="relative border-l border-white/10 ml-3 space-y-6">
            {replies.map((r) => (
              <li key={r.id} className="ml-6" data-testid={`issue-reply-${r.id}`}>
                <span className="absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full bg-zinc-900 border border-white/10 text-[10px] font-medium text-white">
                  {initials(r.user_name || r.user_email)}
                </span>
                <div className="bg-zinc-900 border border-white/10 rounded-lg p-4">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
                    <div className="text-sm">
                      <span className="font-medium text-white">
                        {r.user_name || r.user_email}
                      </span>
                      {r.user_email && r.user_name && (
                        <span className="text-xs text-zinc-500 ml-2">({r.user_email})</span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500" title={r.created_at}>
                      {timeAgo(r.created_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap mb-2">
                    {r.status_change ? (
                      <div className="text-xs text-zinc-400 flex items-center gap-2">
                        Changed status:
                        {r.previous_status && <Pill kind="status" value={r.previous_status} />}
                        <span className="text-zinc-500">→</span>
                        <Pill kind="status" value={r.status_change} />
                      </div>
                    ) : (
                      <Pill kind="status" value={r.current_status || issue.status} />
                    )}
                    {r.team_name && (
                      <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-zinc-400 bg-zinc-950 border border-white/10 rounded-full px-2 py-0.5">
                        <TeamIcon className="w-3 h-3" /> {r.team_name}
                      </span>
                    )}
                  </div>
                  <ReplyAssignTimelineBadges reply={r} referenceData={ref} />
                  <p className="text-sm text-zinc-200 whitespace-pre-wrap">{r.message}</p>
                  {Array.isArray(r.files) && r.files.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {r.files.map((f) => (
                        <a
                          key={f.id}
                          href={`${api.defaults.baseURL}/files/${f.id}/download?auth=${encodeURIComponent(localStorage.getItem("ops_token") || "")}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs text-sky-400 hover:underline bg-zinc-950 border border-white/10 rounded-md px-2 py-1"
                          data-testid={`issue-reply-file-${f.id}`}
                        >
                          <FileText className="w-3.5 h-3.5" />
                          <span className="truncate max-w-[180px]">{f.filename || f.name}</span>
                          <Download className="w-3 h-3 opacity-60" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* Reply form */}
      {canReply ? (
        <section
          className="bg-zinc-900 border border-white/10 rounded-lg p-5 space-y-4"
          data-testid="issue-reply-form"
        >
          <div className="label-overline">Add reply</div>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write an update…"
            className="bg-zinc-950 border-white/10 text-white min-h-[100px]"
            data-testid="issue-reply-message"
          />
          <FileUpload value={files} onChange={setFiles} testid="issue-reply-files" />
          {(() => {
            // Filter the status dropdown for this user.
            // - Admins always see every status.
            // - If `issues_status_change` is off → status dropdown is hidden entirely
            //   (they simply can't change status on reply).
            // - Else → only their `visible_ticket_statuses` are offered.
            const allStatuses = (ref.issue_statuses || ref.statuses) || [];
            const em = new Set(user?.enabled_modules || []);
            const isAdmin = user?.role === "admin";
            let allowedStatuses;
            if (isAdmin) {
              allowedStatuses = allStatuses;
            } else if (!em.has("issues_status_change")) {
              allowedStatuses = [];
            } else {
              const whitelist = new Set(user?.visible_ticket_statuses || []);
              allowedStatuses = allStatuses.filter((s) => whitelist.has(s));
            }
            const showStatusPicker = allowedStatuses.length > 0;
            return (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4 flex-wrap">
              {showStatusPicker && (
              <div className="flex items-center gap-2">
                <Label className="text-xs text-zinc-400">Change status</Label>
                <Select value={statusChange} onValueChange={setStatusChange}>
                  <SelectTrigger
                    className="bg-zinc-950 border-white/10 text-white w-44"
                    data-testid="issue-reply-status"
                  >
                    <SelectValue placeholder="No change" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100">
                    {allowedStatuses.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {statusChange && (
                  <button
                    type="button"
                    onClick={() => setStatusChange("")}
                    className="text-xs text-zinc-500 hover:text-white"
                  >
                    Clear
                  </button>
                )}
              </div>
              )}
              <ReplyAssignControls
                assignTo={assignTo}
                setAssignTo={setAssignTo}
                forwardTo={forwardTo}
                setForwardTo={setForwardTo}
                referenceData={ref}
                kind="issue"
                forwardEnabled={
                  user?.role === "admin" ||
                  (user?.enabled_modules || []).includes("issues_forward_to")
                }
              />
            </div>
            <Button
              onClick={submit}
              disabled={sending}
              className="bg-white text-black hover:bg-zinc-200 font-medium"
              data-testid="issue-reply-submit"
            >
              <Send className="w-4 h-4 mr-1.5" />
              {sending ? "Posting…" : "Post reply"}
            </Button>
          </div>
            );
          })()}
        </section>
      ) : (
        <div className="text-xs text-zinc-500 italic">
          You have read-only access to tickets. Ask an admin if you need to reply.
        </div>
      )}
    </div>
  );
}
